import { useCallback, useEffect, useRef, useState } from "react";
import type { QuotaResponse } from "../../../shared/contracts/billing";
import type { QueryResponse } from "../../../shared/contracts/query";
import { CandidatePicker } from "./components/CandidatePicker";
import { ClarifyingQuestion, type ClarifyingAnswerValue } from "./components/ClarifyingQuestion";
import { CountryGrid } from "./components/CountryGrid";
import { CountryDetail } from "./components/CountryDetail";
import { SearchForm, type SearchRequestInput } from "./components/SearchForm";
import { getDataStatus, getQuota, QueryClientError, queryCustoms, redeemCode, submitFeedback, type QueryRequestWithSession } from "./api/query-client";
import { QuotaPanel } from "./components/QuotaPanel";
import { AppShell } from "../../components/AppShell";

function localToday(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

type AppProps = {
  defaultRuleDate?: string;
};

type StatusError = string | null;

function toSafeError(error: unknown): string {
  if (error instanceof QueryClientError) return error.message;
  return "查询暂时不可用，请稍后重试";
}

function toStatusError(error: unknown): string {
  if (error instanceof QueryClientError && error.code === "aborted") return "";
  return "数据状态暂时不可用";
}

function displayDateTime(value: string | null): string {
  if (!value) return "未提供";
  return value.replace("T", " ").replace(".000Z", " UTC");
}

function buildAnswerRequest(
  request: QueryRequestWithSession,
  question: NonNullable<QueryResponse["nextQuestion"]>,
  value: ClarifyingAnswerValue,
): QueryRequestWithSession {
  const preserved = {
    query: request.query,
    ruleDate: request.ruleDate,
    attributes: request.attributes,
    ...(request.querySessionId ? { querySessionId: request.querySessionId } : {}),
    ...(request.codeCountry ? { codeCountry: request.codeCountry } : {}),
    ...(request.selectedHs6 ? { selectedHs6: request.selectedHs6 } : {}),
  };
  if (question.attribute === "codeCountry" && typeof value === "string" && (value === "CN" || value === "US" || value === "CA")) {
    return { ...preserved, codeCountry: value };
  }
  return {
    ...preserved,
    attributes: { ...request.attributes, [question.attribute]: value },
  };
}

export function App({ defaultRuleDate = localToday() }: AppProps) {
  const [request, setRequest] = useState<QueryRequestWithSession | null>(null);
  const [response, setResponse] = useState<QueryResponse | null>(null);
  const [dataStatus, setDataStatus] = useState<QueryResponse["dataStatus"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<StatusError>(null);
  const [pending, setPending] = useState(false);
  const [expandedCountry, setExpandedCountry] = useState<QueryResponse["results"][number]["country"] | null>(null);
  const [quota, setQuota] = useState<QuotaResponse | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const activeRequest = useRef<AbortController | null>(null);
  const activeStatus = useRef<{ controller: AbortController; generation: number } | null>(null);
  const activeQuota = useRef<AbortController | null>(null);
  const statusGeneration = useRef(0);
  const queryDataVersion = useRef(0);

  const refreshQuota = useCallback(() => {
    activeQuota.current?.abort();
    const controller = new AbortController();
    activeQuota.current = controller;
    setQuotaLoading(true);
    getQuota(controller.signal)
      .then((nextQuota) => {
        if (controller.signal.aborted || activeQuota.current !== controller) return;
        setQuota(nextQuota);
      })
      .catch(() => {
        if (!controller.signal.aborted && activeQuota.current === controller) setQuota(null);
      })
      .finally(() => {
        if (activeQuota.current === controller) {
          activeQuota.current = null;
          setQuotaLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    refreshQuota();
    return () => {
      activeQuota.current?.abort();
      activeQuota.current = null;
    };
  }, [refreshQuota]);

  useEffect(() => {
    const controller = new AbortController();
    const generation = statusGeneration.current;
    const queryVersion = queryDataVersion.current;
    activeStatus.current = { controller, generation };
    getDataStatus(controller.signal, defaultRuleDate)
      .then((status) => {
        if (
          controller.signal.aborted
          || statusGeneration.current !== generation
          || queryDataVersion.current !== queryVersion
          || activeStatus.current?.controller !== controller
        ) return;
        setDataStatus(status);
        setStatusError(null);
      })
      .catch((reason: unknown) => {
        if (
          !controller.signal.aborted
          && statusGeneration.current === generation
          && queryDataVersion.current === queryVersion
          && activeStatus.current?.controller === controller
        ) {
          setStatusError(toStatusError(reason));
        }
      })
      .finally(() => {
        if (activeStatus.current?.controller === controller) activeStatus.current = null;
      });
    return () => {
      controller.abort();
      if (activeStatus.current?.controller === controller) activeStatus.current = null;
    };
  }, [defaultRuleDate]);

  const submitRequest = useCallback((nextRequest: QueryRequestWithSession) => {
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    setRequest(nextRequest);
    setResponse(null);
    setExpandedCountry(null);
    setError(null);
    setPending(true);
    queryCustoms(nextRequest, controller.signal)
      .then((nextResponse) => {
        if (controller.signal.aborted || activeRequest.current !== controller) return;
        queryDataVersion.current += 1;
        setResponse(nextResponse);
        setDataStatus(nextResponse.dataStatus);
        setStatusError(null);
        setError(null);
        setRequest((current) => current ? { ...current, querySessionId: nextResponse.queryId } : current);
        if (
          nextResponse.dataStatus.ready
          && !nextResponse.testData
          && nextResponse.nextQuestion === null
          && (nextResponse.results.length > 0 || nextResponse.candidates.length > 0)
        ) {
          refreshQuota();
        }
      })
      .catch((reason: unknown) => {
        if (activeRequest.current !== controller || controller.signal.aborted) return;
        setError(toSafeError(reason));
      })
      .finally(() => {
        if (!controller.signal.aborted && activeRequest.current === controller) setPending(false);
      });
  }, [refreshQuota]);

  useEffect(() => () => {
    statusGeneration.current += 1;
    queryDataVersion.current += 1;
    activeStatus.current?.controller.abort();
    activeStatus.current = null;
    activeRequest.current?.abort();
    activeRequest.current = null;
  }, []);

  function handleSearch(input: SearchRequestInput) {
    submitRequest({ query: input.query, ruleDate: input.ruleDate, attributes: input.attributes });
  }

  function handleCandidateSelect(hs6: string) {
    if (!request) return;
    const selectedCandidates = response?.candidates.filter((candidate) => candidate.hs6 === hs6) ?? [];
    const sourceSnapshotId = response?.dataStatus.sourceSnapshotId;
    if (response?.dataStatus.ready && !response.testData && sourceSnapshotId) {
      // A group choice is evidence for every national candidate shown in that
      // group.  Feedback is best-effort and never blocks the official query.
      void Promise.allSettled(selectedCandidates.flatMap((candidate) => {
        const sourceId = candidate.classificationSourceIds[0];
        const source = response.sources.find((item) => item.id === sourceId);
        if (!source) return [];
        return [submitFeedback({
          eventType: "accepted_candidate",
          sourceReleaseId: source.releaseId,
          sourceSnapshotId,
          queryId: response.queryId,
          candidateId: candidate.candidateId,
          payload: { interaction: "selected_hs6" },
        })];
      })).catch(() => undefined);
    }
    submitRequest({
      query: request.query,
      ruleDate: request.ruleDate,
      attributes: request.attributes,
      ...(request.querySessionId ? { querySessionId: request.querySessionId } : {}),
      ...(request.codeCountry ? { codeCountry: request.codeCountry } : {}),
      selectedHs6: hs6,
    });
  }

  function handleQuestionAnswer(attribute: string, value: ClarifyingAnswerValue) {
    if (!request || !response?.nextQuestion) return;
    if (attribute !== response.nextQuestion.attribute) return;
    submitRequest(buildAnswerRequest(request, response.nextQuestion, value));
  }

  function retry() {
    if (request) submitRequest(request);
  }

  const handleRedeem = useCallback(async (code: string, confirmRestore?: boolean) => {
    const result = await redeemCode(code, confirmRestore === true);
    setQuota(result.quota);
  }, []);

  const reviewed = dataStatus?.ready === true;
  const statusLabel = dataStatus === null ? "正在读取数据状态" : reviewed ? "数据已就绪" : "测试版";
  const expandedResult = response && expandedCountry !== null
    ? response.results.find((result) => result.country === expandedCountry) ?? null
    : null;

  return (
    <AppShell
      active="query"
      mainId="query-workspace"
      title="Customs Clearance · Duties · Delivery"
      subtitle="中国出口视角 · 查询中国、美国、加拿大对应编码与进口要求"
      status={(
        <details className="status-strip">
          <summary>
            <span className={`status-badge${reviewed ? " ready" : ""}`}>{statusLabel}</span>
            <span>查看数据状态</span>
          </summary>
          <div className="status-detail" aria-label="数据状态">
            {statusError ? <span>{statusError}</span> : null}
            {dataStatus ? (
              <>
                <span>evaluatedAt：{displayDateTime(dataStatus.evaluatedAt)}</span>
                <span>lastSourceCheckAt：{displayDateTime(dataStatus.lastSourceCheckAt)}</span>
                <span>数据就绪：{dataStatus.ready ? "已审核" : "待审核"}</span>
                <ul className="status-reasons">
                  {dataStatus.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                </ul>
              </>
            ) : <span>数据就绪状态由查询 API 返回</span>}
          </div>
        </details>
      )}
    >
        <section className="query-intro" aria-labelledby="query-workspace-heading">
          <p className="eyebrow">关务查询工作台</p>
          <h2 id="query-workspace-heading">一次查询，对照中美加关务要求</h2>
          <p>输入中文品名或 HS 编码，查看三国候选编码、基础税率、附加税与贸易救济风险。</p>
        </section>
        <section className="service-banner" aria-label="跨境物流与报关服务">
          <div className="service-banner-copy">
            <h2>跨境物流与报关服务</h2>
            <p>中美加门到门运输 · 报关资料预审 · 人工关务支持</p>
          </div>
          <div className="service-banner-contact">
            <span>电话 / 微信同号</span>
            <a href="tel:19064012025" aria-label="拨打电话 19064012025">190 6401 2025</a>
          </div>
        </section>
        <div className="query-layout">
          <section className="query-column" aria-label="查询条件">
            <SearchForm defaultRuleDate={defaultRuleDate} pending={pending} onSubmit={handleSearch} />
            <p className="disclosure">数据仅供参考，核对后才能用于报关，不能直接作为报关或税费结论。</p>
            <p className="feedback-note">如果有改进建议，请发送邮件至 <a href="mailto:admin@riskcustoms.com">admin@riskcustoms.com</a></p>
          </section>

          <section className="result-column" aria-label="查询结果区域">
            {!pending && !error && !response ? (
              <section className="coverage-panel" aria-label="查询覆盖范围">
                <div className="coverage-item coverage-cn"><strong>CN</strong><span>中国出口编码</span><small>归类候选与申报提示</small></div>
                <div className="coverage-item coverage-us"><strong>US</strong><span>美国进口税费</span><small>基础税、301 与 ADD/CVD 风险</small></div>
                <div className="coverage-item coverage-ca"><strong>CA</strong><span>加拿大进口税费</span><small>MFN、GST、Surtax 与 SIMA 风险</small></div>
              </section>
            ) : null}

            {pending ? <p className="loading-line" aria-live="polite">正在查询…</p> : null}

            {error ? (
              <section className="error-panel" role="alert">
                <h2>查询未完成</h2>
                <p>{error}</p>
                {request ? <button className="secondary-button" type="button" onClick={retry}>重试</button> : null}
              </section>
            ) : null}

            {!pending && response?.results.length === 0 && response?.candidates.length ? (
              <CandidatePicker candidates={response.candidates} onSelect={handleCandidateSelect} />
            ) : null}

            {!pending && response?.nextQuestion ? (
              <ClarifyingQuestion question={response.nextQuestion} onAnswer={handleQuestionAnswer} pending={pending} />
            ) : null}

            {!pending && response && response.results.length > 0 ? (
              <section className="result-panel" aria-labelledby="result-heading">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">查询结果</p>
                    <h2 id="result-heading">对应编码、税率与文件摘要</h2>
                  </div>
                  <span className="muted-text">规则日期：{response.ruleDate}</span>
                  <button className="secondary-button" type="button" onClick={retry}>重试</button>
                </div>
                <CountryGrid
                  results={response.results}
                  expandedCountry={expandedCountry}
                  onExpand={(country) => setExpandedCountry((current) => current === country ? null : country)}
                />
                {expandedResult ? <CountryDetail result={expandedResult} sources={response.sources} /> : null}
                {request ? (
                  <section className="country-detail-panel" aria-labelledby="amazon-search-heading">
                    <h3 id="amazon-search-heading">Amazon 搜索</h3>
                    <p><a href={`https://www.amazon.com/s?k=${encodeURIComponent(request.query)}`} target="_blank" rel="noreferrer">打开“{request.query}”的 Amazon 搜索结果</a></p>
                    <p className="muted-text">商品与价格以 Amazon 页面为准。</p>
                  </section>
                ) : null}
              </section>
            ) : null}

            {!pending && !error && response && response.results.length === 0 && response.candidates.length === 0 ? (
              <p className="empty-line" role="status">暂无匹配结果，请核对品名、编码与规则日期。</p>
            ) : null}

            {!pending && !error && !response ? <p className="empty-line">输入中文品名或编码开始查询。</p> : null}
          </section>
        </div>
        <QuotaPanel quota={quota} loading={quotaLoading} onRedeem={handleRedeem} />
    </AppShell>
  );
}
